import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import { AnalyticsQueryParams, AnalyticsTrendQueryParams } from '../analytics-shared';
import { CommunicationOverview, CommunicationTrendPoint, TimePatternPoint } from './communication-analytics-api.models';

/**
 * Pure API service for communication analytics HTTP operations
 * No state management — just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class CommunicationAnalyticsApiService {
  private readonly logger = inject(LoggerService).scope('CommunicationAnalyticsApiService');

  private readonly baseUrl = `${environment.API_BASE_URL}/protected/care/analytics/communication`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /protected/care/analytics/communication/overview
   * Aggregated conversation engagement metrics for a date range.
   */
  public getOverview(params: AnalyticsQueryParams): Observable<CommunicationOverview> {
    const httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    return this.http.get<CommunicationOverview>(`${this.baseUrl}/overview`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching communication overview:', error);
        return throwError(() => new Error('Failed to fetch communication overview'));
      })
    );
  }

  /**
   * GET /protected/care/analytics/communication/trend
   * Time-bucketed engagement and emotional tone for chart visualization.
   * bucket_size defaults to 'week' on the backend if not provided.
   */
  public getTrend(params: AnalyticsTrendQueryParams): Observable<CommunicationTrendPoint[]> {
    let httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    if (params.bucket_size) {
      httpParams = httpParams.set('bucket_size', params.bucket_size);
    }

    return this.http.get<CommunicationTrendPoint[]>(`${this.baseUrl}/trend`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching communication trend:', error);
        return throwError(() => new Error('Failed to fetch communication trend'));
      })
    );
  }

  /**
   * GET /protected/care/analytics/communication/time-pattern
   * 24-hour session distribution for activity pattern visualization.
   */
  public getTimePattern(params: AnalyticsQueryParams): Observable<TimePatternPoint[]> {
    const httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    return this.http.get<TimePatternPoint[]>(`${this.baseUrl}/time-pattern`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching communication time pattern:', error);
        return throwError(() => new Error('Failed to fetch communication time pattern'));
      })
    );
  }
}
